import { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { buildErrorMessage, ifcRootGuid } from '@constants'
import { AxiosError } from 'axios'
import produce from 'immer'
import { findIndex } from 'lodash-es'
import { useDebouncedCallback } from 'use-debounce'
import { TextField } from '@mui/material'
import { useControlStore } from '@editor/stores'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useIfcFiles } from '@editorHooks'
import { getProjectFiles } from '@queries'
import { postFileMetadata } from '@mutations'

const IFCFileTitleField = () => {
  const { projectId } = useParams()
  const activeIfcFileGuid = useControlStore(state => state.activeIfcFileGuid)
  const glbFiles = useIfcFiles()
  const activeFile = activeIfcFileGuid
    ? glbFiles.find((f: FileItem) => f.guid === activeIfcFileGuid) ?? null
    : null

  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const lastSavedNameRef = useRef(activeFile?.name ?? '')

  useEffect(() => {
    lastSavedNameRef.current = activeFile?.name ?? ''
  }, [activeFile?.guid, activeFile?.name])

  const { mutate: saveFileMetadata, isLoading: isSaving } = useMutation({
    mutationFn: async (config: { item_guid: string; name: string }) => {
      if (!projectId || !activeFile) return activeFile as FileItem
      return postFileMetadata.request(projectId, ifcRootGuid, {
        ...(activeFile.additional_data || {}),
        item_guid: config.item_guid,
        name: config.name,
      })
    },
    onSuccess: (data: FileItem) => {
      lastSavedNameRef.current = data.name
      const queryKey = getProjectFiles.getKey(projectId!, ifcRootGuid)
      queryClient.setQueryData<FilesResult>(
        queryKey,
        produce<FilesResult>(draft => {
          if (!draft) return
          const index = findIndex(draft.files, { guid: data.guid })
          if (index !== -1) draft.files[index].name = data.name
        }),
      )
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Titels'), {
        variant: 'error',
      })
    },
    mutationKey: ['saveFileMetadata', projectId, ifcRootGuid, activeIfcFileGuid],
    meta: {
      title: 'IFC-Datei Titel speichern',
      description: 'Speichert den Titel der IFC-Datei in der Datenbank.',
    },
  })

  const debouncedSaveName = useDebouncedCallback(() => {
    if (!projectId || !activeIfcFileGuid) return
    const queryKey = getProjectFiles.getKey(projectId, ifcRootGuid)
    const data = queryClient.getQueryData<FilesResult>(queryKey)
    const file = data?.files?.find((f: FileItem) => f.guid === activeIfcFileGuid)
    const trimmed = file?.name?.trim()
    if (trimmed) {
      saveFileMetadata({ item_guid: activeIfcFileGuid, name: trimmed })
    }
  }, 3000)

  if (!activeFile) return null

  return (
    <TextField
      fullWidth
      label="Name"
      value={activeFile.name ?? ''}
      onChange={e => {
        const name = e.target.value
        const queryKey = getProjectFiles.getKey(projectId!, ifcRootGuid)
        queryClient.setQueryData<FilesResult>(
          queryKey,
          produce<FilesResult>(draft => {
            if (!draft) return
            const index = findIndex(draft.files, { guid: activeFile.guid })
            if (index !== -1) draft.files[index].name = name
          }),
        )
        debouncedSaveName()
      }}
      onBlur={() => debouncedSaveName.flush()}
      disabled={isSaving}
      helperText={isSaving ? 'Wird gespeichert…' : undefined}
      size="small"
    />
  )
}

export default IFCFileTitleField
